import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Lock, Mail, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../components/ui/input-otp';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.mfaRequired) {
        setMfaRequired(true);
        setTempToken(result.tempToken);
        toast.info('Please enter your 2FA code');
      } else if (result.success) {
        toast.success('Login successful!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await verifyMfa(tempToken, totpCode);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
      setTotpCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout min-h-screen">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-[#0B1C2D]">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2BBBAD] to-[#64FFDA] flex items-center justify-center shadow-lg shadow-[#2BBBAD]/20">
              <Shield className="w-7 h-7 text-[#0B1C2D]" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-[#E6F1FF] tracking-tight">PanicLock</h1>
              <p className="text-sm text-[#8892B0]">Unified Digital Emergency Lock</p>
            </div>
          </div>

          {!mfaRequired ? (
            <>
              <h2 className="text-3xl font-bold text-[#E6F1FF] mb-2">Welcome back</h2>
              <p className="text-[#8892B0] mb-8">Sign in to your secure account</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#E6F1FF]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8892B0]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-11 h-12 bg-[#112240] border-[#2BBBAD]/20 text-[#E6F1FF] placeholder:text-[#8892B0] focus:border-[#64FFDA] focus:ring-[#64FFDA]/20"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#E6F1FF]">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8892B0]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-11 pr-11 h-12 bg-[#112240] border-[#2BBBAD]/20 text-[#E6F1FF] placeholder:text-[#8892B0] focus:border-[#64FFDA] focus:ring-[#64FFDA]/20"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8892B0] hover:text-[#E6F1FF]"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold rounded-lg shadow-lg shadow-[#2BBBAD]/20 hover:shadow-[#2BBBAD]/40 transition-all duration-200"
                  data-testid="login-submit-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <p className="mt-6 text-center text-[#8892B0]">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#2BBBAD] hover:text-[#64FFDA] font-medium">
                  Create one
                </Link>
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-[#E6F1FF] mb-2">Two-Factor Authentication</h2>
              <p className="text-[#8892B0] mb-8">Enter the 6-digit code from your authenticator app</p>

              <form onSubmit={handleMfaSubmit} className="space-y-6">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={totpCode}
                    onChange={setTotpCode}
                    data-testid="mfa-otp-input"
                  >
                    <InputOTPGroup className="gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          className="w-12 h-14 text-xl font-bold bg-[#112240] border-[#2BBBAD]/30 text-[#E6F1FF] rounded-lg"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  className="w-full h-12 bg-[#2BBBAD] hover:bg-[#3FD2C7] text-[#0B1C2D] font-bold rounded-lg shadow-lg shadow-[#2BBBAD]/20 hover:shadow-[#2BBBAD]/40 transition-all duration-200"
                  data-testid="mfa-submit-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Verify'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setMfaRequired(false);
                    setTotpCode('');
                  }}
                  className="w-full text-[#8892B0] hover:text-[#E6F1FF] text-sm"
                >
                  Back to login
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1762429648253-b8bc921c8b30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxiYW5rJTIwYnVpbGRpbmclMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBuaWdodHxlbnwwfHx8fDE3NzA4MDY5NDl8MA&ixlib=rb-4.1.0&q=85"
          alt="Modern architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1C2D]/90 via-[#0B1C2D]/70 to-[#2BBBAD]/30" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12">
          <div className="glass-card p-6 rounded-2xl max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-[#2BBBAD]" />
              <span className="text-sm font-semibold text-[#2BBBAD]">Protected by PanicLock</span>
            </div>
            <h3 className="text-2xl font-bold text-[#E6F1FF] mb-2">
              One Button. Total Security.
            </h3>
            <p className="text-[#8892B0]">
              Instantly lock all your financial accounts with a single tap. 
              Advanced protection when you need it most.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
